/*
 * Select — Selector tipo dropdown con búsqueda
 * ─────────────────────────────────────────────────────────────────
 *
 * Props:
 *   label        string    Etiqueta sobre el input
 *   value        any       Valor seleccionado (corresponde a option.value)
 *   onChange     fn        Recibe el valor de la opción seleccionada (option.value)
 *   options      array     Lista de opciones: [{ name: string, value: any }, ...]
 *   mapper       object    Mapeo de campos cuando options no usa { name, value }.
 *                          Formato: { name: 'campoNombre', value: 'campoValor' }
 *                          Ej: mapper={{ name: 'name', value: 'code' }}
 *   placeholder  string    Texto cuando no hay selección (default: "Seleccionar...")
 *   icon         string    Ícono a la izquierda (nombre Material Icons, opcional)
 *   searchable   bool      Muestra campo de búsqueda (default: true)
 *   disabled     bool      Deshabilita el componente
 *   error        bool      Activa estilos de error externos
 *   errorMessage string    Texto del mensaje de error externo
 *   body         fn        (data) => ReactNode. Renderiza el contenido de cada opción y del valor
 *                          seleccionado. data es el objeto normalizado de la opción ({ name, value, ...rest }).
 *
 * Uso básico:
 *   <Select label="País" value={val} onChange={setVal}
 *           options={[{ name: "México", value: "mx" }, { name: "Colombia", value: "co" }]} />
 *
 * Con mapper (cuando los datos vienen con campos distintos):
 *   <Select label="Cliente" value={val} onChange={setVal}
 *           options={clientes} mapper={{ name: 'name', value: 'code' }} />
 *
 * Sin búsqueda:
 *   <Select label="Tipo" value={val} onChange={setVal} options={opts} searchable={false} />
 *
 * Con ícono personalizado:
 *   <Select label="Rol" value={val} onChange={setVal} options={opts} icon="manage_accounts" />
 *
 * Con validación:
 *   <Select label="Estado" value={val} onChange={setVal} options={opts}
 *           error={!!errors.estado} errorMessage={errors.estado} />
 *
 * Con body (render personalizado de opciones):
 *   <Select label="Usuario" value={val} onChange={setVal} options={usuarios}
 *           body={(data) => (
 *             <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
 *               <img src={data.avatar} width={24} height={24} style={{ borderRadius: "50%" }} />
 *               <span>{data.name}</span>
 *             </div>
 *           )} />
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Text from "../Text/Text";
import "./Select.scss";

const Select = ({ 
    label,
    style,
    value,
    onChange,
    className,
    options = [],
    mapper = { name: 'name', value: 'value' },
    placeholder = "Seleccionar...",
    icon,
    searchable = true,
    disabled = false,
    error = false,
    errorMessage = "Campo requerido",
    marginBottom = true,
    cleaner = true,
    body = null
}) => {
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);
    const searchRef = useRef(null);
    const isOpenRef = useRef(false);
    const dropdownRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    const calcPos = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom - 14, left: rect.left, width: rect.width });
    };

    const normalizedOptions = options.map((o) => ({
        ...o,
        name:  String(o[mapper?.name  ?? 'name']  ?? "").trim(),
        value: o[mapper?.value ?? 'value'],
    }));

    const valueKey = mapper?.value ?? 'value';
    const selectedValue = value != null && typeof value === 'object' ? value[valueKey] : value;
    const selected = normalizedOptions.find((o) => o.value === selectedValue) ?? null;

    const filtered = search.trim()
        ? normalizedOptions.filter((o) =>
              String(o.name).toLowerCase().includes(search.toLowerCase())
          )
        : normalizedOptions;

    // Cerrar al hacer click fuera
    useEffect(() => {
        const handleOutside = (e) => {
            if (
                isOpenRef.current &&
                ref.current &&
                !ref.current.contains(e.target) &&
                !(dropdownRef.current && dropdownRef.current.contains(e.target))
            ) {
                isOpenRef.current = false;
                setOpen(false);
                setClosing(true);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    useEffect(() => {
        if (!open) return;
        const update = () => calcPos();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open]);

    // Flip vertical y ajuste horizontal si el dropdown se sale de la pantalla
    useLayoutEffect(() => {
        if (!open || !dropdownRef.current || !ref.current) return;
        const dropRect = dropdownRef.current.getBoundingClientRect();
        const rect = ref.current.getBoundingClientRect();
        let top = dropdownPos.top;
        let left = dropdownPos.left;
        if (dropRect.bottom > window.innerHeight - 8) {
            top = rect.top - dropRect.height + 14;
        }
        if (dropRect.right > window.innerWidth - 8) {
            left = window.innerWidth - dropRect.width - 8;
        }
        if (top !== dropdownPos.top || left !== dropdownPos.left) {
            setDropdownPos({ top, left, width: rect.width });
        }
    }, [open]);

    // Foco en búsqueda al abrir
    useEffect(() => {
        if (open && searchable && searchRef.current) {
            searchRef.current.focus();
        }
    }, [open, searchable]);

    const handleOpen = () => {
        if (disabled || isOpenRef.current || closing) return;
        calcPos();
        isOpenRef.current = true;
        setOpen(true);
        setSearch("");
    };

    const handleClose = () => {
        if (!isOpenRef.current) return;
        isOpenRef.current = false;
        setOpen(false);
        setClosing(true);
    };

    const handleAnimEnd = () => {
        if (closing) setClosing(false);
    };

    const handleSelect = (opt) => {
        onChange?.(opt);
        handleClose();
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange?.(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            handleClose();
            // Saltar al siguiente elemento focusable
            const focusables = Array.from(
                document.querySelectorAll(
                    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            );
            const trigger = ref.current?.querySelector(".sel-trigger");
            const idx = focusables.indexOf(trigger);
            if (idx !== -1 && focusables[idx + 1]) focusables[idx + 1].focus();
        }
    };

    const showError = error;

    return (
        <div
            style={style}
            className={`input-field ${className} Select ${showError ? "has-error" : ""} ${open ? "is-open" : ""} animate__animated animate__fadeInUp`}
            ref={ref}
        >
            {label && (
                <Text size="small" color="var(--gray-dark-500)" className="input-label">
                    {label}
                </Text>
            )}

            {/* Trigger — mismo look que input-element */}
            <div className={`input-wrapper ${marginBottom ? "" : "no-margin-bottom"}`}>
                {icon && <span className="icon input-icon" data-icon={icon} />}
                <button
                    type="button"
                    className={`input-element sel-trigger ${icon ? "has-left-icon" : ""} ${disabled ? "disabled" : ""}`}
                    onClick={handleOpen}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                >
                    <span className={`sel-value ${!selected ? "sel-placeholder" : ""}`}>
                        {selected
                            ? (body ? body(selected) : selected.name)
                            : placeholder}
                    </span>
                </button>

                {/* Ícono chevron fijo a la derecha */}
                <span
                    className={`icon sel-icon-right ${open ? "sel-icon-open" : ""}`}
                    data-icon="expand_more"
                />

                {/* Botón limpiar */}
                {selected && !disabled && cleaner && (
                    <button
                        className="preview icon"
                        data-icon="close"
                        type="button"
                        onClick={handleClear}
                    />
                )}
            </div>

            {showError && (
                <Text className="input-error" color="var(--red-600)" size="small">
                    {errorMessage}
                </Text>
            )}

            {/* Dropdown */}
            {(open || closing) && createPortal(
                <div className="Select" style={{ position: "fixed", top: 0, left: 0, width: 0, height: 0, overflow: "visible", zIndex: 9999, pointerEvents: "none" }}>
                <div
                    ref={dropdownRef}
                    className={`sel-dropdown ${closing ? "sel-closing" : "sel-opening"}`}
                    style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, minWidth: `${dropdownPos.width}px`, pointerEvents: "all" }}
                    onAnimationEnd={handleAnimEnd}
                    onMouseDown={(e) => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}
                    role="listbox"
                >
                    {searchable && (
                        <div className="sel-search-wrap">
                            <span className="icon sel-search-icon" data-icon="search" />
                            <input
                                ref={searchRef}
                                className="sel-search"
                                type="text"
                                placeholder="Buscar..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {search && (
                                <button
                                    className="sel-search-clear icon"
                                    data-icon="close"
                                    type="button"
                                    onClick={() => setSearch("")}
                                />
                            )}
                        </div>
                    )}

                    <ul className="sel-list">
                        {filtered.length === 0 ? (
                            <li className="sel-empty">Sin resultados</li>
                        ) : (
                            filtered.map((opt, i) => (
                                <li
                                    key={opt.value ?? i}
                                    className={`sel-option ${opt.value === selectedValue ? "selected" : ""}`}
                                    onClick={() => handleSelect(opt)}
                                    role="option"
                                    aria-selected={opt.value === selectedValue}
                                >
                                    {body ? body(opt) : opt.name}
                                    {opt.value === selectedValue && (
                                        <span className="icon sel-check" data-icon="check" />
                                    )}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Select;
